const img = (id, w = 1400) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

export const SERVICE_PROCESS = [
  { key: "Consultation", desc: "Tell us about your project, goals and what you want to change." },
  { key: "Site Assessment", desc: "We review the property, existing space and project requirements." },
  { key: "Planning", desc: "We establish the scope of work, project requirements and next steps." },
  { key: "Construction", desc: "Our team completes the construction work for your project." },
  { key: "Finishing", desc: "Surfaces, details and finishing work are completed." },
  { key: "Final Walkthrough", desc: "We walk through the finished project and review the final details." },
];

const GALLERY = {
  basement: [
    img("photo-1600607687939-ce8a6c25118c", 1000),
    img("photo-1600210492486-724fe5c67fb0", 1000),
    img("photo-1600607687920-4e2a09cf159d", 1000),
  ],
  exterior: [
    img("photo-1600566753086-00f18fb6b3ea", 1000),
    img("photo-1600047509807-ba8f99d2cdde", 1000),
    img("photo-1512917774080-9991f1c4c750", 1000),
  ],
  kitchen: [
    img("photo-1556911220-bff31c812dba", 1000),
    img("photo-1556909212-d5b604d0c90d", 1000),
    img("photo-1600210492486-724fe5c67fb0", 1000),
  ],
  build: [
    img("photo-1541888946425-d81bb19240f5", 1000),
    img("photo-1504307651254-35680f356dfd", 1000),
    img("photo-1621905251189-08b45d6a269e", 1000),
  ],
};

export const SERVICE_PAGES = [
  {
    path: "/framing-contractor-edmonton",
    service: "Framing",
    seoTitle: "Framing Contractor Edmonton",
    eyebrow: "Framing Contractor in Edmonton",
    h1: "Professional Framing for Edmonton Homes.",
    heroCopy:
      "Cando Construction Services Ltd. provides structural wood framing for new builds, renovations and home additions across Edmonton.",
    introTitle: "The Structure Everything Else Depends On.",
    intro: [
      "Framing sets the shape, strength and squareness of everything that follows. Cando Construction provides professional wood framing for Edmonton homeowners, builders and renovation projects.",
      "From new construction to opening up existing spaces, we frame with the details in mind — straight walls, solid connections and a job site that stays organized from start to finish.",
    ],
    included: [
      { t: "New Construction Framing", d: "Structural wood framing for new residential builds." },
      { t: "Renovation Framing", d: "Framing for remodels, layout changes and opened-up spaces." },
      { t: "Addition Framing", d: "Framing for home additions, tied into the existing structure." },
      { t: "Basement Framing", d: "Wall and bulkhead framing for basement development." },
    ],
    benefits: [
      { t: "Straight & True", d: "Accurate framing that makes every finishing trade's work better." },
      { t: "Clear Communication", d: "Know what's happening and what's next at every stage." },
      { t: "Organized Job Sites", d: "A clean, orderly site keeps your project moving." },
      { t: "Local Experience", d: "Framing suited to Edmonton homes and local conditions." },
    ],
    faqs: [
      { q: "Do you frame home additions?", a: "Yes. Addition framing is one of our core framing services, tied properly into your existing structure." },
      { q: "Do you take on renovation framing?", a: "Yes. We frame for renovations and layout changes, including new walls, openings and basement development." },
      { q: "Do you frame new home construction?", a: "Yes, we provide structural wood framing for new residential builds." },
      { q: "How do I get a framing estimate?", a: "Use the form on this page or call us. Tell us about your project and we'll arrange the next step." },
    ],
    heroImg: img("photo-1504307651254-35680f356dfd", 1600),
    gallery: GALLERY.build,
    galleryLabel: "Recent Framing Work",
  },
  {
    path: "/home-additions-edmonton",
    service: "Home Addition",
    seoTitle: "Home Additions Edmonton",
    eyebrow: "Home Additions in Edmonton",
    h1: "Create More Space Without Leaving Home.",
    heroCopy:
      "Cando Construction Services Ltd. plans and builds home additions for Edmonton homeowners — from structural framing through finished interiors.",
    introTitle: "More Room, Built Around Your Home.",
    intro: [
      "A well-built addition gives your family the space it needs without leaving the neighbourhood you love. Cando Construction builds home additions for Edmonton homeowners, handling the project from planning through completion.",
      "We work directly with you from the first walkthrough, keeping the scope clear, the job site organized and the project moving toward a finished space that feels like it was always part of your home.",
    ],
    included: [
      { t: "Addition Planning", d: "Review your goals, property and project requirements." },
      { t: "Structural Framing", d: "Professional framing tied into the existing home." },
      { t: "Exterior Finishing", d: "Exterior work that blends with your home." },
      { t: "Interior Finishing", d: "Drywall, painting and finishing for a complete space." },
    ],
    benefits: [
      { t: "One Point of Contact", d: "Work directly with our team throughout your addition." },
      { t: "Straightforward Process", d: "From walkthrough to punch list, expectations stay clear." },
      { t: "Quality Workmanship", d: "Details that matter long after the walkthrough." },
      { t: "Edmonton Experience", d: "Additions designed around local homes and seasons." },
    ],
    faqs: [
      { q: "What types of home additions do you build?", a: "We build residential additions that expand your living space, handling framing through interior finishing. Tell us what you're planning and we'll review the requirements." },
      { q: "Do you handle the framing and the finishing?", a: "Yes. Our services cover the structural framing as well as drywall, painting and interior finishing." },
      { q: "How long does a home addition take?", a: "Every addition is different. After a site assessment we establish the scope of work and the expected project sequence." },
      { q: "How do I start planning an addition?", a: "Request an estimate through the form on this page or call us. We'll discuss your project and arrange a site assessment." },
    ],
    heroImg: img("photo-1600047509807-ba8f99d2cdde", 1600),
    gallery: GALLERY.exterior,
    galleryLabel: "Recent Addition Projects",
  },
  {
    path: "/basement-renovations-edmonton",
    service: "Basement Renovation",
    seoTitle: "Basement Renovations Edmonton",
    eyebrow: "Basement Renovations in Edmonton",
    h1: "Turn Your Basement Into Space You'll Actually Use.",
    heroCopy:
      "Cando Construction Services Ltd. provides basement renovation and finishing services for Edmonton homeowners, from framing and drywall through interior finishing.",
    introTitle: "A Basement Built Around Your Home.",
    intro: [
      "An unfinished or outdated basement is square footage your family isn't using. Cando Construction transforms Edmonton basements into comfortable, functional living areas.",
      "We handle the full scope — framing, drywall, painting and finishing — with clear communication and an organized job site from the first walkthrough to the final punch list.",
    ],
    included: [
      { t: "Framing", d: "Wall and bulkhead framing for your new basement layout." },
      { t: "Drywall", d: "Installation, joint finishing and surface preparation." },
      { t: "Painting", d: "Professional interior painting and finishing." },
      { t: "Bathroom Renovation", d: "Basement bathroom renovation and finishing." },
      { t: "Finishing", d: "The interior details that make the space feel complete." },
      { t: "Waterproofing", d: "Help protect your basement from moisture and water infiltration." },
    ],
    benefits: [
      { t: "Full-Scope Service", d: "Framing through finishing, handled by one team." },
      { t: "Clear Communication", d: "Know what's happening and where your project stands." },
      { t: "Organized Job Sites", d: "We respect your home and keep the site clean." },
      { t: "Local Experience", d: "Basements built for Edmonton homes and conditions." },
    ],
    faqs: [
      { q: "Do you handle full basement renovations?", a: "Yes. Basement renovation and finishing is one of our core services, from framing and drywall through interior finishing." },
      { q: "Can you add a bathroom to my basement?", a: "Yes, bathroom renovation is part of our service offering and can be included in your basement project." },
      { q: "Do you offer basement waterproofing?", a: "Yes. We provide basement waterproofing to help protect against moisture and water infiltration." },
      { q: "How do I get an estimate for my basement?", a: "Use the form on this page or call us. Tell us about your space and what you want to change." },
    ],
    heroImg: img("photo-1598928506311-c55ded91a20c", 1600),
    gallery: GALLERY.basement,
    galleryLabel: "Recent Basement Projects",
  },
  {
    path: "/kitchen-renovations-edmonton",
    service: "Kitchen Renovation",
    seoTitle: "Kitchen Renovations Edmonton",
    eyebrow: "Kitchen Renovations in Edmonton",
    h1: "A Kitchen Designed Around How You Live.",
    heroCopy:
      "Cando Construction Services Ltd. provides kitchen renovation services for Edmonton homeowners — planned around your family's needs, layout and lifestyle.",
    introTitle: "The Hardest-Working Room, Done Right.",
    intro: [
      "A kitchen renovation changes how your whole home works. Cando Construction renovates Edmonton kitchens with the layout, surfaces and finishes your family actually needs.",
      "From the first walkthrough to the final punch list, we keep communication clear and the job site organized, moving your project steadily toward completion.",
    ],
    included: [
      { t: "Layout & Planning", d: "A renovation planned around your space and how you use it." },
      { t: "Construction", d: "The structural and renovation work behind your new kitchen." },
      { t: "Drywall & Surface Prep", d: "Clean surfaces ready for finishing." },
      { t: "Painting & Finishing", d: "Professional finishing that completes the room." },
    ],
    benefits: [
      { t: "Planned Around You", d: "A kitchen designed around your family's needs and lifestyle." },
      { t: "One Point of Contact", d: "Work directly with the team throughout the project." },
      { t: "Quality Workmanship", d: "Details that hold up to daily use." },
      { t: "Straightforward Process", d: "Clear expectations from walkthrough to punch list." },
    ],
    faqs: [
      { q: "Do you handle full kitchen renovations?", a: "Yes. We provide kitchen renovation services including construction, drywall, painting and finishing work." },
      { q: "Can you change my kitchen layout?", a: "Yes, layout changes can be part of your renovation. We'll review your space and requirements during the assessment." },
      { q: "How disruptive is a kitchen renovation?", a: "Renovations are real construction, but we keep the job site organized and communication clear so you always know what's next." },
      { q: "How do I get started?", a: "Request an estimate through the form on this page or call us to discuss your kitchen." },
    ],
    heroImg: img("photo-1556909212-d5b604d0c90d", 1600),
    gallery: GALLERY.kitchen,
    galleryLabel: "Recent Kitchen Projects",
  },
  {
    path: "/bathroom-renovations-edmonton",
    service: "Bathroom Renovation",
    seoTitle: "Bathroom Renovations Edmonton",
    eyebrow: "Bathroom Renovations in Edmonton",
    h1: "Better Bathrooms. Better Everyday Living.",
    heroCopy:
      "Cando Construction Services Ltd. modernizes Edmonton bathrooms with professional renovation and finishing work.",
    introTitle: "A Bathroom That Works as Well as It Looks.",
    intro: [
      "A bathroom renovation is one of the most-used upgrades in any home. Cando Construction renovates Edmonton bathrooms with careful construction and clean finishing.",
      "We manage the project from the initial walkthrough through the final punch list, keeping your home respected and the process clear.",
    ],
    included: [
      { t: "Renovation Construction", d: "The construction work behind your new bathroom." },
      { t: "Drywall & Prep", d: "Installation, joint finishing and surface preparation." },
      { t: "Painting", d: "Professional interior painting and finishing." },
      { t: "Finishing Details", d: "The details that make the room feel complete." },
    ],
    benefits: [
      { t: "Professional Finishing", d: "Clean lines and surfaces done properly." },
      { t: "Clear Communication", d: "Know what's happening at every stage." },
      { t: "Respect for Your Home", d: "Organized, tidy job sites throughout." },
      { t: "One Point of Contact", d: "Deal directly with our team, start to finish." },
    ],
    faqs: [
      { q: "Do you renovate bathrooms of all sizes?", a: "Yes. We renovate everything from powder rooms to full main bathrooms. Tell us about your space." },
      { q: "Do you handle the drywall and painting?", a: "Yes. Drywall installation, joint finishing and painting are part of our in-house services." },
      { q: "Can a bathroom be part of a basement renovation?", a: "Yes, bathroom renovation can be included as part of a larger basement project." },
      { q: "How do I get a bathroom estimate?", a: "Use the form on this page or call us to discuss your project." },
    ],
    heroImg: img("photo-1552321554-5fefe8c9ef14", 1600),
    gallery: GALLERY.basement,
    galleryLabel: "Recent Bathroom Projects",
  },
  {
    path: "/decks-fencing-edmonton",
    service: "Deck / Fencing",
    seoTitle: "Decks & Fencing Edmonton",
    eyebrow: "Decks & Fencing in Edmonton",
    h1: "Outdoor Spaces Built for Edmonton Seasons.",
    heroCopy:
      "Cando Construction Services Ltd. builds decks and fencing for Edmonton homeowners — outdoor structures built to handle changing seasons.",
    introTitle: "Built for the Backyard You Actually Use.",
    intro: [
      "A well-built deck or fence changes how you use your yard. Cando Construction builds outdoor structures for Edmonton homeowners, designed with local conditions in mind.",
      "From structure to finish, we keep the project organized and the communication clear, so you know exactly what's happening and when.",
    ],
    included: [
      { t: "Deck Construction", d: "Outdoor structures built to handle Edmonton's seasons." },
      { t: "Fencing", d: "Fencing built properly, posts to finish." },
      { t: "Exterior Painting", d: "Professional exterior painting and finishing." },
      { t: "Repairs & Rebuilds", d: "Renovation of existing outdoor structures." },
    ],
    benefits: [
      { t: "Season-Ready Builds", d: "Structures designed around Edmonton's climate." },
      { t: "Quality Workmanship", d: "Construction details that last." },
      { t: "Organized Job Sites", d: "Your yard respected throughout the build." },
      { t: "Straightforward Process", d: "Clear scope and next steps from day one." },
    ],
    faqs: [
      { q: "Do you build both decks and fences?", a: "Yes. Decks and fencing are both part of our outdoor construction services." },
      { q: "Do you handle exterior painting too?", a: "Yes, we provide exterior painting with professional preparation and finishing." },
      { q: "When is the best season to build a deck?", a: "Decks can be planned any time of year. Contact us to discuss your project and timing." },
      { q: "How do I get an estimate?", a: "Use the form on this page or call us to talk about your outdoor project." },
    ],
    heroImg: img("photo-1605146769289-440113cc3d00", 1600),
    gallery: GALLERY.exterior,
    galleryLabel: "Recent Outdoor Projects",
  },
  {
    path: "/painting-contractor-edmonton",
    service: "Painting",
    seoTitle: "Painting Contractor Edmonton",
    eyebrow: "Painting Contractor in Edmonton",
    h1: "Interior & Exterior Painting, Professionally Finished.",
    heroCopy:
      "Cando Construction Services Ltd. provides interior and exterior painting for Edmonton homeowners, with professional preparation and finishing.",
    introTitle: "Preparation Is the Difference.",
    intro: [
      "A quality paint job is mostly preparation. Cando Construction paints Edmonton interiors and exteriors with the surface prep and finishing work that makes the result last.",
      "As a full-service construction company, we can also handle the drywall and surface repairs that painting often uncovers.",
    ],
    included: [
      { t: "Interior Painting", d: "Walls, ceilings and interior surfaces professionally finished." },
      { t: "Exterior Painting", d: "Exterior painting with proper preparation." },
      { t: "Surface Preparation", d: "The prep work that makes paint last." },
      { t: "Drywall Repair", d: "Surface repairs handled before finishing." },
    ],
    benefits: [
      { t: "Proper Prep", d: "Surfaces prepared before a drop of finish goes on." },
      { t: "Full-Service Team", d: "Drywall and repairs handled by the same crew." },
      { t: "Respect for Your Home", d: "Clean, organized work inside and out." },
      { t: "Clear Communication", d: "You always know what's next." },
    ],
    faqs: [
      { q: "Do you do both interior and exterior painting?", a: "Yes. We provide interior and exterior painting with professional preparation and finishing." },
      { q: "Can you repair drywall before painting?", a: "Yes. Drywall installation, joint finishing and surface preparation are part of our services." },
      { q: "Do you paint as part of renovations?", a: "Yes, painting is often the finishing stage of our renovation projects, and is also available as a standalone service." },
      { q: "How do I get a painting estimate?", a: "Use the form on this page or call us to discuss your project." },
    ],
    heroImg: img("photo-1562259949-e8e7689d7828", 1600),
    gallery: GALLERY.kitchen,
    galleryLabel: "Recent Painting & Finishing",
  },
  {
    path: "/drywall-finishing-edmonton",
    service: "Drywall / Finishing",
    seoTitle: "Drywall & Finishing Edmonton",
    eyebrow: "Drywall & Finishing in Edmonton",
    h1: "Clean Lines. Smooth Finishes. Professional Results.",
    heroCopy:
      "Cando Construction Services Ltd. provides professional drywall installation, joint finishing and surface preparation for Edmonton homeowners.",
    introTitle: "The Finish Every Room Depends On.",
    intro: [
      "Drywall is the surface you see every day — and the one that shows every shortcut. Cando Construction installs and finishes drywall for Edmonton homes with the preparation that makes paint and finishes look their best.",
      "From basement development to renovation repairs, we deliver clean lines and smooth surfaces, ready for finishing.",
    ],
    included: [
      { t: "Drywall Installation", d: "Professional board installation for new and renovated spaces." },
      { t: "Joint Finishing", d: "Taping and finishing for smooth, seamless surfaces." },
      { t: "Surface Preparation", d: "Surfaces prepared properly for paint and finishes." },
      { t: "Repairs", d: "Drywall repair as part of renovation projects." },
    ],
    benefits: [
      { t: "Smooth Results", d: "Finishing that looks right in every light." },
      { t: "Renovation-Ready", d: "Drywall integrated with framing and painting services." },
      { t: "Organized Job Sites", d: "Dust and mess managed with respect for your home." },
      { t: "One Point of Contact", d: "Deal directly with our team throughout." },
    ],
    faqs: [
      { q: "Do you install and finish drywall?", a: "Yes. We provide drywall installation, joint finishing and surface preparation." },
      { q: "Do you handle basement drywall?", a: "Yes, basement development is one of our most common drywall projects, often alongside framing and painting." },
      { q: "Can you repair damaged drywall?", a: "Yes, drywall repairs can be included as part of your renovation or painting project." },
      { q: "How do I get an estimate?", a: "Use the form on this page or call us to discuss your project." },
    ],
    heroImg: img("photo-1621905251189-08b45d6a269e", 1600),
    gallery: GALLERY.build,
    galleryLabel: "Recent Drywall & Finishing",
  },
  {
    path: "/basement-waterproofing-edmonton",
    service: "Waterproofing",
    seoTitle: "Basement Waterproofing Edmonton",
    eyebrow: "Basement Waterproofing in Edmonton",
    h1: "Protect Your Basement From Moisture.",
    heroCopy:
      "Cando Construction Services Ltd. provides basement waterproofing for Edmonton homeowners, helping protect your basement from moisture and water infiltration.",
    introTitle: "A Dry Basement Is a Usable Basement.",
    intro: [
      "Moisture is the enemy of every finished basement. Cando Construction provides basement waterproofing for Edmonton homeowners, helping protect your space from water infiltration.",
      "Whether you're protecting a finished space or preparing to build one, we'll assess your situation and recommend the appropriate next step.",
    ],
    included: [
      { t: "Moisture Assessment", d: "Review of your basement's moisture and infiltration concerns." },
      { t: "Waterproofing", d: "Work to help protect against water infiltration." },
      { t: "Pre-Finishing Protection", d: "Waterproofing before basement development." },
      { t: "Restoration", d: "Framing, drywall and finishing after water issues are addressed." },
    ],
    benefits: [
      { t: "Protect Your Investment", d: "Safeguard finished spaces and stored belongings." },
      { t: "Full-Service Team", d: "Restoration and finishing handled by the same company." },
      { t: "Local Experience", d: "Knowledge of Edmonton homes and local conditions." },
      { t: "Clear Communication", d: "Straight answers about your situation and options." },
    ],
    faqs: [
      { q: "Do you waterproof basements?", a: "Yes. We provide basement waterproofing to help protect against moisture and water infiltration." },
      { q: "Should I waterproof before finishing my basement?", a: "Addressing moisture before finishing is strongly recommended. We can assess your basement and include waterproofing in your renovation plan." },
      { q: "Can you restore a basement after water damage?", a: "We can handle the framing, drywall and finishing work to restore your space once water issues are addressed." },
      { q: "How do I get started?", a: "Use the form on this page or call us to discuss your basement." },
    ],
    heroImg: img("photo-1585704032915-c3400ca199e7", 1600),
    gallery: GALLERY.basement,
    galleryLabel: "Recent Basement Work",
  },
];
